/**
 * ClinicianRoundsPage — fused deterministic hand-off for clinicians (June 2026).
 *
 * Clinician-side ONLY, passcode-gated (same clinician/founder passcode as the
 * Founder Vault). Patient intake is never touched. Combines the legacy
 * 218 legacy symptom engines with the 89 physical-exam / ECG / lab engines (307 total)
 * findings into a single ranked-Bayesian SBAR via POST /api/clinician/rounds.
 * The report is SHA-256 hash-chained server-side for FRE 902(13) audit replay
 * and can be exported one-click to a hash-stamped, validator-ready PDF.
 * NO LLM anywhere — pure deterministic engines.
 */
import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Stethoscope, Activity, FlaskConical, HeartPulse, ChevronDown,
  AlertTriangle, ClipboardList, Layers, Sparkles, ShieldAlert, ShieldCheck, Scale,
  Lock, Unlock, FileDown, Link2, BadgeCheck, Boxes,
  TrendingUp, TrendingDown, Minus, LineChart,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PASS_KEY = 'pai_rounds_passcode';

const SEV = {
  CRITICAL: { badge: 'bg-red-600 text-white', card: 'border-red-300 bg-red-50', text: 'text-red-700', rank: 4 },
  HIGH: { badge: 'bg-orange-500 text-white', card: 'border-orange-300 bg-orange-50', text: 'text-orange-700', rank: 3 },
  MODERATE: { badge: 'bg-amber-500 text-white', card: 'border-amber-300 bg-amber-50', text: 'text-amber-700', rank: 2 },
  LOW: { badge: 'bg-slate-500 text-white', card: 'border-slate-300 bg-slate-50', text: 'text-slate-600', rank: 1 },
};
const sevMeta = (s) => SEV[(s || 'LOW').toUpperCase()] || SEV.LOW;

const CHANNELS = [
  { key: 'exam', label: 'Physical Exam Findings', icon: HeartPulse, accent: 'text-rose-600',
    placeholder: 'e.g. positive Murphy\u2019s sign with RUQ tenderness; bilateral pitting pedal oedema; nuchal rigidity with positive Kernig sign' },
  { key: 'ecg', label: 'ECG Findings', icon: Activity, accent: 'text-violet-600',
    placeholder: 'e.g. 2 mm ST elevation in II, III and aVF with reciprocal depression; QTc of 520 ms; irregularly irregular with absent p waves' },
  { key: 'labs', label: 'Lab Results', icon: FlaskConical, accent: 'text-teal-600',
    placeholder: 'e.g. lactate 5.2; Na 118; troponin elevated and rising; pH 7.15, HCO3 10; glucose 28, ketones 5.2' },
];

const EXAMPLE = {
  age: '61', sex: 'male',
  chiefComplaint: 'crushing central chest pain',
  symptoms: 'pain radiating to left arm, sweating, nausea for 40 minutes',
  medications: 'amlodipine',
  existingConditions: 'hypertension, type 2 diabetes',
  notes: '',
  exam: 'bilateral pitting pedal oedema to the knees; raised JVP',
  ecg: '2 mm ST elevation in leads II, III and aVF with reciprocal depression; sinus tachycardia',
  labs: 'troponin elevated with a significant rise; lactate 4.2; creatinine 180',
};

const CHANNEL_META = {
  symptom: { label: 'Symptom Engines', icon: Stethoscope, accent: 'text-sky-600' },
  exam: { label: 'Physical Exam', icon: HeartPulse, accent: 'text-rose-600' },
  ecg: { label: 'ECG', icon: Activity, accent: 'text-violet-600' },
  labs: { label: 'Labs', icon: FlaskConical, accent: 'text-teal-600' },
};

const esc = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function buildPrintableHtml(report, opts = {}) {
  const sb = report.sbar || {};
  const c = opts.counts || { catalog: 307, legacy: 218, channel: 88, meta: 1 };
  // Deep-link to the PUBLIC /verify page (auto-runs the check on load) — not the raw
  // API endpoint — so a single QR scan gives a court-verifiable Verified/Not-found answer.
  const verifyPageUrl = opts.verifyPageUrl
    || `${(typeof window !== 'undefined' && window.location.origin) || ''}/verify?hash=${encodeURIComponent(report.contentHash || '')}`;
  const qrDataUrl = opts.qrDataUrl || '';
  const diffs = (report.ranked_differentials || []).slice(0, 10).map((d) =>
    `<tr><td>${esc(d.dx)}</td><td style="text-align:right;font-weight:700">${d.post_test != null ? Math.round(d.post_test * 100) + '%' : '\u2014'}</td><td style="text-align:right">${d.combined_lr}</td></tr>`
  ).join('');
  const actions = (report.critical_actions || []).map((a) =>
    `<li><strong>[${esc(a.severity)}] ${esc(a.category)}</strong><br>${esc(a.message)}</li>`
  ).join('');
  const conflicts = (report.conflicts || []).map((c) => `<li>${esc(c.note)}</li>`).join('');
  const channelBlocks = ['symptom', 'exam', 'ecg', 'labs'].map((ch) => {
    const items = (report.by_channel && report.by_channel[ch]) || [];
    if (!items.length) return '';
    const rows = items.map((f) =>
      `<li><strong>[${esc(f.severity)}] ${esc(f.category)}</strong><br>${esc(f.message)}</li>`).join('');
    return `<h3>${esc((CHANNEL_META[ch] || {}).label || ch)} (${items.length})</h3><ul>${rows}</ul>`;
  }).join('');
  const verifyBadge = qrDataUrl ? `<div class="verify-badge">
      <div class="vb-pill">&#10003; VERIFIED</div>
      <img src="${qrDataUrl}" width="118" height="118" alt="Scan to verify this hand-off" />
      <div class="vb-cap">Scan to auto-verify in P.Ai&rsquo;s append-only audit ledger</div>
      <div class="vb-url">${esc(verifyPageUrl)}</div>
    </div>` : '';
  return `<!doctype html><html><head><meta charset="utf-8"><title>P.Ai Clinician Rounds — SBAR Hand-off</title>
<style>
  body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#1e293b;font-size:14px;line-height:1.55;margin:32px;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
  h1{font-size:22px;margin:0 0 4px;} h2{font-size:16px;color:#0f766e;margin:22px 0 8px;border-bottom:2px solid #ccfbf1;padding-bottom:4px;}
  h3{font-size:14px;margin:14px 0 4px;color:#334155;}
  .sub{color:#64748b;font-size:13px;margin:0 0 14px;}
  .sev{display:inline-block;padding:3px 12px;border-radius:999px;font-weight:700;color:#fff;font-size:13px;}
  .chain{background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px;font-size:12px;color:#475569;word-break:break-all;margin:12px 0;}
  .chain-row{display:flex;gap:18px;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;}
  .chain-meta{flex:1;min-width:240px;}
  .verify-badge{text-align:center;border:1px solid #a7f3d0;background:#f0fdf4;border-radius:10px;padding:10px 12px;width:160px;box-sizing:border-box;}
  .verify-badge img{display:block;margin:6px auto;border:1px solid #e2e8f0;border-radius:6px;}
  .vb-pill{display:inline-block;background:#059669;color:#fff;font-weight:800;font-size:12px;letter-spacing:.05em;padding:3px 12px;border-radius:999px;}
  .vb-cap{font-size:10.5px;color:#047857;font-weight:600;line-height:1.35;}
  .vb-url{font-size:8.5px;color:#94a3b8;word-break:break-all;margin-top:4px;}
  table{width:100%;border-collapse:collapse;font-size:13px;} td,th{border-bottom:1px solid #e2e8f0;padding:6px 8px;text-align:left;}
  ul{margin:4px 0 4px 18px;padding:0;} li{margin-bottom:8px;}
  .lbl{font-weight:700;color:#0f766e;text-transform:uppercase;font-size:12px;letter-spacing:.04em;}
  .foot{margin-top:26px;border-top:1px solid #e2e8f0;padding-top:10px;color:#94a3b8;font-size:12px;}
</style></head><body>
  <h1>P.Ai Clinician Rounds — SBAR Hand-off</h1>
  <p class="sub">Deterministic fused report · ${c.catalog} engines (${c.legacy} symptom + ${report.new_engine_count || c.channel} exam/ECG/lab + ${c.meta} meta) · NO LLM in the diagnostic loop</p>
  <p>Overall acuity: <span class="sev" style="background:${{CRITICAL:'#dc2626',HIGH:'#f97316',MODERATE:'#f59e0b',LOW:'#64748b'}[(report.severity||'LOW').toUpperCase()]||'#64748b'}">${esc(report.severity)}</span>
     &nbsp; ${report.flag_count} reconciled finding(s)</p>
  <div class="chain">
    <div class="chain-row">
      <div class="chain-meta">
        <strong>Audit hash-chain (FRE 902(13))</strong><br>
        Generated: ${esc(report.generatedAt)}<br>
        Content SHA-256: ${esc(report.contentHash)}<br>
        Previous hash: ${esc(report.previousHash)}
        <div style="margin-top:10px"><a href="${verifyPageUrl}" style="display:inline-block;background:#059669;color:#fff;text-decoration:none;padding:5px 12px;border-radius:999px;font-weight:700;font-size:12px">&#10003; Verify this document independently</a></div>
      </div>
      ${verifyBadge}
    </div>
  </div>
  <h2>SBAR</h2>
  <p><span class="lbl">Situation</span><br>${esc(sb.situation)}</p>
  <p><span class="lbl">Background</span><br>${esc(sb.background)}</p>
  <p><span class="lbl">Assessment</span><br>${esc(sb.assessment)}</p>
  <p><span class="lbl">Recommendation</span><br>${esc(sb.recommendation)}</p>
  ${diffs ? `<h2>Bayesian-ranked differentials</h2><table><tr><th>Diagnosis</th><th style="text-align:right">Post-test</th><th style="text-align:right">Combined LR</th></tr>${diffs}</table>` : ''}
  ${conflicts ? `<h2>Conflicts to reconcile</h2><ul>${conflicts}</ul>` : ''}
  ${actions ? `<h2>High-priority actions</h2><ul>${actions}</ul>` : ''}
  ${channelBlocks ? `<h2>All findings by channel</h2>${channelBlocks}` : ''}
  <div class="foot">Generated by P.Ai Global Health deterministic clinical engines citing named published guidelines — not AI or large language models. Hash-chained for audit replay.</div>
</body></html>`;
}

function ChannelAccordion({ channel, value, onChange }) {
  const Icon = channel.icon;
  const filled = (value || '').trim().length > 0;
  return (
    <details
      className="group rounded-xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md"
      data-testid={`${channel.key}-accordion`}
    >
      <summary
        className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4 select-none"
        data-testid={`${channel.key}-accordion-summary`}
      >
        <span className="flex items-center gap-3">
          <Icon className={`h-5 w-5 ${channel.accent}`} />
          <span className="text-base font-semibold text-slate-800">{channel.label}</span>
          {filled && (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
              entered
            </span>
          )}
        </span>
        <ChevronDown className="h-5 w-5 text-slate-400 transition-transform duration-200 group-open:rotate-180" />
      </summary>
      <div className="px-5 pb-5">
        <Textarea
          rows={3}
          value={value}
          onChange={(e) => onChange(channel.key, e.target.value)}
          placeholder={channel.placeholder}
          className="resize-y text-sm"
          data-testid={`${channel.key}-textarea`}
        />
      </div>
    </details>
  );
}

function FlagRow({ f }) {
  const m = sevMeta(f.severity);
  return (
    <div className={`rounded-lg border ${m.card} px-4 py-3`} data-testid="rounds-flag-row">
      <div className="flex items-start justify-between gap-3">
        <span className="text-sm font-semibold text-slate-800">{f.category}</span>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${m.badge}`}>{f.severity}</span>
      </div>
      <p className="mt-1 text-sm leading-relaxed text-slate-600">{f.message}</p>
      {Array.isArray(f.evidence) && f.evidence.length > 0 && (
        <p className="mt-1.5 text-xs text-slate-400">Evidence: {f.evidence.join(' \u00b7 ')}</p>
      )}
    </div>
  );
}

function LockScreen({ onUnlock }) {
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const attempt = useCallback(async () => {
    if (!passcode.trim()) return;
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/vault/auth`, { passcode });
      sessionStorage.setItem(PASS_KEY, passcode);
      onUnlock(passcode);
    } catch (e) {
      setError('Invalid passcode. Clinician/founder access only.');
    } finally {
      setBusy(false);
    }
  }, [passcode, onUnlock]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 px-6" data-testid="rounds-lock-screen">
      <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-800 p-8 shadow-2xl">
        <div className="mb-5 flex items-center gap-3">
          <Lock className="h-7 w-7 text-teal-400" />
          <h1 className="text-xl font-bold text-white">Clinician Rounds — Access</h1>
        </div>
        <p className="mb-6 text-sm text-slate-300">
          This deterministic clinician hand-off view is restricted. Enter the clinician/founder passcode to continue.
        </p>
        <Input
          type="password"
          value={passcode}
          onChange={(e) => setPasscode(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && attempt()}
          placeholder="Passcode"
          className="mb-3 bg-slate-900 text-white"
          data-testid="rounds-passcode-input"
        />
        {error && <p className="mb-3 text-sm text-red-400" data-testid="rounds-lock-error">{error}</p>}
        <Button onClick={attempt} disabled={busy} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="rounds-unlock-button">
          {busy ? 'Verifying\u2026' : 'Unlock'}
        </Button>
      </div>
    </div>
  );
}

function LedgerTimeline({ ledger, currentHash }) {
  if (!ledger) return null;
  const blocks = [...(ledger.blocks || [])].reverse(); // newest first
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="rounds-ledger">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-base font-semibold text-slate-800">
          <Boxes className="h-5 w-5 text-teal-600" /> Tamper-evident ledger
        </h3>
        <span
          className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${ledger.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}
          data-testid="rounds-ledger-status"
        >
          <ShieldCheck className="h-3.5 w-3.5" /> {ledger.ok ? 'INTACT' : 'BROKEN'} · {ledger.length} block{ledger.length === 1 ? '' : 's'}
        </span>
      </div>
      <p className="mb-3 text-xs text-slate-400">
        Append-only · each block hash binds to the previous one, so altering any past record breaks every block after it.
      </p>
      <div className="relative pl-5">
        <div className="absolute bottom-1 left-[7px] top-1 w-px bg-slate-200" />
        {blocks.length === 0 && <p className="text-sm text-slate-400">No sealed blocks yet — run rounds to seal the first.</p>}
        {blocks.map((b) => {
          const isCurrent = currentHash && b.contentHash === currentHash;
          const m = sevMeta(b.severity);
          return (
            <div
              key={b.position}
              className={`relative flex items-start gap-3 py-2 ${isCurrent ? '-ml-3 rounded-lg bg-teal-50 px-3' : ''}`}
              data-testid="rounds-ledger-block"
            >
              <span className={`absolute -left-[13px] mt-1.5 h-3 w-3 rounded-full ${m.badge} ring-2 ring-white`} />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-bold text-slate-700">Block #{b.position}</span>
                  {isCurrent && <span className="rounded-full bg-teal-600 px-1.5 py-0.5 text-[10px] font-bold text-white">THIS REPORT</span>}
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${m.badge}`}>{b.severity}</span>
                </div>
                <p className="truncate font-mono text-[11px] text-slate-400" title={b.contentHash}>{(b.contentHash || '').slice(0, 32)}{'\u2026'}</p>
                <p className="text-[11px] text-slate-400">Sealed {b.sealedAt ? new Date(b.sealedAt).toLocaleString() : '\u2014'} · {b.flagCount} finding(s)</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Longitudinal trend surface (Phase 1, July 2026) ──────────────────────
// Read-only sparkline + direction chip for the tracked numeric metrics, backed
// by GET /api/patient/longitudinal-metrics. Observational only — this panel
// never touches triage, flags, or the rounds report.
const TREND_META = {
  hba1c: { label: 'HbA1c', unit: '%' },
  egfr: { label: 'eGFR', unit: 'mL/min' },
  opioid_mme: { label: 'Opioid dose', unit: 'MME/day' },
  phq9: { label: 'PHQ-9', unit: 'pts' },
  gad7: { label: 'GAD-7', unit: 'pts' },
};
const TREND_ORDER = ['hba1c', 'egfr', 'opioid_mme', 'phq9', 'gad7'];
const DIR_META = {
  worsening: { cls: 'bg-red-100 text-red-700 border-red-200', color: '#dc2626', label: 'Worsening' },
  improving: { cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', color: '#059669', label: 'Improving' },
  stable: { cls: 'bg-slate-100 text-slate-600 border-slate-200', color: '#475569', label: 'Stable' },
  insufficient_data: { cls: 'bg-slate-50 text-slate-400 border-slate-200', color: '#94a3b8', label: 'Single reading' },
};

function MetricSparkline({ points, color }) {
  const vals = (points || []).map((p) => p.value);
  const w = 132, h = 40, pad = 5;
  if (vals.length === 0) return null;
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = (max - min) || 1;
  const n = vals.length;
  const x = (i) => pad + (n === 1 ? (w - 2 * pad) / 2 : (i * (w - 2 * pad)) / (n - 1));
  const y = (v) => pad + (h - 2 * pad) * (1 - (v - min) / span);
  const d = vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label="trend sparkline">
      {n > 1 && <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
      {vals.map((v, i) => <circle key={i} cx={x(i)} cy={y(v)} r={i === n - 1 ? 3 : 1.9} fill={color} />)}
    </svg>
  );
}

function MetricTrendCard({ trend, points }) {
  const meta = TREND_META[trend.metric] || { label: trend.label, unit: trend.unit };
  const dm = DIR_META[trend.clinical_direction] || DIR_META.stable;
  const NumIcon = trend.numeric_direction === 'rising' ? TrendingUp
    : trend.numeric_direction === 'falling' ? TrendingDown : Minus;
  const crossing = (trend.threshold_crossings || [])[0];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`trend-card-${trend.metric}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-medium text-slate-500">{meta.label}</p>
          <p className="text-2xl font-bold text-slate-900" data-testid={`trend-latest-${trend.metric}`}>
            {trend.latest}<span className="ml-1 text-xs font-normal text-slate-400">{meta.unit}</span>
          </p>
        </div>
        <span className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${dm.cls}`} data-testid={`trend-dir-${trend.metric}`}>
          <NumIcon className="h-3 w-3" /> {dm.label}
        </span>
      </div>
      <div className="mt-2">
        <MetricSparkline points={points} color={dm.color} />
      </div>
      <div className="mt-1 space-y-0.5 text-[11px] leading-tight text-slate-500">
        {trend.count > 1 && (
          <p>{trend.count} readings · Δ {trend.delta > 0 ? '+' : ''}{trend.delta}{trend.metric === 'egfr' && trend.slope_per_year != null ? ` · ${trend.slope_per_year}/yr` : ''}</p>
        )}
        {trend.severity_band && <p>Band: {trend.severity_band}{trend.band_transition ? ` (${trend.band_transition})` : ''}</p>}
        {crossing && <p className="text-amber-600">Crossed {crossing.threshold} ({crossing.direction})</p>}
      </div>
    </div>
  );
}

function LongitudinalTrendsPanel() {
  const [id, setId] = useState('');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    const q = id.trim();
    if (!q) return;
    setBusy(true); setErr(''); setData(null);
    try {
      let res = await axios.get(`${API}/patient/longitudinal-metrics`, { params: { encounterId: q } });
      if (!res.data?.tracked) {
        const res2 = await axios.get(`${API}/patient/longitudinal-metrics`, { params: { patient_id: q } });
        if (res2.data?.tracked) res = res2;
      }
      setData(res.data);
      if (!res.data?.tracked) setErr('No longitudinal metrics recorded for this ID yet.');
    } catch (e) {
      setErr('Could not load trends. Check the ID and try again.');
    } finally {
      setBusy(false);
    }
  }, [id]);

  const trendsByMetric = Object.fromEntries((data?.trends || []).map((t) => [t.metric, t]));
  const metrics = data?.metrics || {};
  const present = TREND_ORDER.filter((m) => trendsByMetric[m]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="rounds-trends-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-base font-semibold text-slate-800">
          <LineChart className="h-5 w-5 text-indigo-600" /> Longitudinal trends
          <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-normal text-indigo-600">observational · never alters triage</span>
        </h2>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Patient ID or Encounter ID"
            value={id}
            onChange={(e) => setId(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            className="h-9 w-56 text-sm"
            data-testid="trends-id-input"
          />
          <Button onClick={load} disabled={busy} className="h-9 bg-indigo-600 text-sm hover:bg-indigo-700" data-testid="trends-load-button">
            {busy ? 'Loading…' : 'Load trends'}
          </Button>
        </div>
      </div>
      {err && <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500" data-testid="trends-error">{err}</p>}
      {!data && !err && (
        <p className="text-sm text-slate-400">
          Enter a patient or encounter ID to see HbA1c, eGFR, opioid MME, PHQ-9 and GAD-7 trajectory at a glance.
        </p>
      )}
      {present.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="trends-grid">
          {present.map((m) => (
            <MetricTrendCard key={m} trend={trendsByMetric[m]} points={(metrics[m] || {}).points} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ClinicianRoundsPage() {
  const [passcode, setPasscode] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [form, setForm] = useState({
    age: '', sex: '', chiefComplaint: '', symptoms: '', medications: '',
    existingConditions: '', notes: '', exam: '', ecg: '', labs: '',
  });
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ledger, setLedger] = useState(null);
  const [counts, setCounts] = useState({ catalog: 307, legacy: 218, channel: 88, meta: 1 });

  useEffect(() => {
    const saved = sessionStorage.getItem(PASS_KEY);
    if (saved) { setPasscode(saved); setUnlocked(true); }
  }, []);

  useEffect(() => {
    axios.get(`${API}/system/build-status`).then(r => {
      const b = r?.data?.engines_breakdown || {};
      setCounts({
        catalog: r?.data?.catalog_engines ?? 307,
        legacy: b.legacy_symptom_intake ?? 218,
        channel: b.clinician_channel ?? 88,
        meta: b.clinician_meta ?? 1,
      });
    }).catch(() => {});
  }, []);

  const loadLedger = useCallback(async (pass) => {
    const code = pass || passcode || sessionStorage.getItem(PASS_KEY);
    if (!code) return;
    try {
      const res = await axios.get(`${API}/clinician/rounds/chain-verify`, { params: { passcode: code } });
      setLedger(res.data);
    } catch (e) { /* ledger is non-critical; ignore */ }
  }, [passcode]);

  useEffect(() => { if (unlocked) loadLedger(); }, [unlocked, loadLedger]);

  const setField = useCallback((k, v) => setForm((p) => ({ ...p, [k]: v })), []);

  const run = useCallback(async () => {
    setBusy(true); setError(''); setReport(null);
    try {
      const res = await axios.post(`${API}/clinician/rounds`, { ...form, passcode });
      setReport(res.data);
      loadLedger(passcode);
    } catch (e) {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem(PASS_KEY);
        setUnlocked(false);
        return;
      }
      setError(e?.response?.data?.detail || 'Failed to run rounds. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [form, passcode, loadLedger]);

  const loadExample = useCallback(() => { setForm(EXAMPLE); setReport(null); setError(''); }, []);

  const exportPdf = useCallback(async () => {
    if (!report) return;
    // Open the print window synchronously inside the click gesture (avoids popup blocking),
    // then generate the QR and write the final hash-verified hand-off.
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write('<!doctype html><title>Preparing\u2026</title><body style="font-family:sans-serif;padding:28px;color:#475569">Preparing hash-verified hand-off\u2026</body>');
    const verifyPageUrl = `${window.location.origin}/verify?hash=${encodeURIComponent(report.contentHash || '')}`;
    let qrDataUrl = '';
    try {
      qrDataUrl = await QRCode.toDataURL(verifyPageUrl, {
        width: 236, margin: 1, errorCorrectionLevel: 'M',
        color: { dark: '#0f172a', light: '#ffffff' },
      });
    } catch (e) { /* QR is optional — the PDF still exports with the text verify link */ }
    w.document.open();
    w.document.write(buildPrintableHtml(report, { verifyPageUrl, qrDataUrl, counts }));
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch (e) { /* user can print manually */ } }, 400);
  }, [report, counts]);

  if (!unlocked) {
    return <LockScreen onUnlock={(p) => { setPasscode(p); setUnlocked(true); }} />;
  }

  const overall = report ? sevMeta(report.severity) : null;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="rounds-page">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900 px-6 py-7">
        <div className="mx-auto flex max-w-6xl items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <Layers className="h-7 w-7 text-teal-400" />
              <h1 className="text-2xl font-bold text-white sm:text-3xl">Clinician Rounds View</h1>
            </div>
            <p className="mt-2 max-w-3xl text-sm text-slate-300">
              Deterministic fused hand-off — the {counts.catalog}-engine platform: the legacy {counts.legacy}-engine symptom pipeline reconciled with the {counts.channel + counts.meta}-engine physical-exam, ECG and lab findings into one ranked-Bayesian SBAR. No LLM in
              the diagnostic loop. Clinician-side only; patient intake is untouched.
            </p>
          </div>
          <span className="hidden items-center gap-1.5 rounded-full bg-slate-800 px-3 py-1.5 text-xs text-teal-300 sm:flex">
            <Unlock className="h-3.5 w-3.5" /> Unlocked
          </span>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-6 pt-6">
        <LongitudinalTrendsPanel />
      </div>

      <div className="mx-auto grid max-w-6xl gap-6 px-6 py-8 lg:grid-cols-2">
        {/* ── Input column ── */}
        <div className="space-y-5">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-base font-semibold text-slate-800">
                <Stethoscope className="h-5 w-5 text-sky-600" /> Patient context
              </h2>
              <button
                onClick={loadExample}
                className="flex items-center gap-1 text-xs font-medium text-teal-700 hover:text-teal-900"
                data-testid="rounds-load-example"
              >
                <Sparkles className="h-3.5 w-3.5" /> Load example
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Age" value={form.age} onChange={(e) => setField('age', e.target.value)} data-testid="rounds-age" />
              <Input placeholder="Sex" value={form.sex} onChange={(e) => setField('sex', e.target.value)} data-testid="rounds-sex" />
            </div>
            <Input className="mt-3" placeholder="Chief complaint" value={form.chiefComplaint} onChange={(e) => setField('chiefComplaint', e.target.value)} data-testid="rounds-cc" />
            <Textarea className="mt-3 text-sm" rows={2} placeholder="Symptoms / history" value={form.symptoms} onChange={(e) => setField('symptoms', e.target.value)} data-testid="rounds-symptoms" />
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Input placeholder="Medications" value={form.medications} onChange={(e) => setField('medications', e.target.value)} data-testid="rounds-meds" />
              <Input placeholder="Existing conditions" value={form.existingConditions} onChange={(e) => setField('existingConditions', e.target.value)} data-testid="rounds-conditions" />
            </div>
          </div>

          {CHANNELS.map((c) => (
            <ChannelAccordion key={c.key} channel={c} value={form[c.key]} onChange={setField} />
          ))}

          <Button
            onClick={run}
            disabled={busy}
            className="w-full bg-teal-600 py-6 text-base font-semibold hover:bg-teal-700"
            data-testid="rounds-run-button"
          >
            {busy ? 'Running deterministic engines\u2026' : 'Run Clinician Rounds'}
          </Button>
          {error && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="rounds-error">
              {error}
            </p>
          )}
        </div>

        {/* ── Report column ── */}
        <div>
          {!report && (
            <div className="flex h-full min-h-[320px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-white/50 p-8 text-center" data-testid="rounds-empty">
              <ClipboardList className="h-10 w-10 text-slate-300" />
              <p className="mt-3 text-sm text-slate-500">
                Enter findings and run rounds to generate the fused SBAR hand-off.
              </p>
            </div>
          )}

          {report && (
            <div className="space-y-5" data-testid="rounds-report-card">
              {/* Severity + counts + export */}
              <div className={`rounded-xl border-2 ${overall.card} p-5`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-500">Overall acuity</span>
                  <span className={`rounded-full px-3 py-1 text-sm font-bold ${overall.badge}`} data-testid="rounds-severity-badge">
                    {report.severity}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-600">
                  <span><strong className="text-slate-900">{report.flag_count}</strong> reconciled finding(s)</span>
                  <span><strong className="text-slate-900">{report.critical_actions?.length || 0}</strong> high-priority action(s)</span>
                  <span><strong className="text-slate-900">{report.ranked_differentials?.length || 0}</strong> ranked differential(s)</span>
                </div>
                <Button
                  onClick={exportPdf}
                  className="mt-4 w-full bg-slate-900 hover:bg-slate-800"
                  data-testid="rounds-export-pdf"
                >
                  <FileDown className="mr-2 h-4 w-4" /> Export SBAR to PDF
                </Button>
                {report.contentHash && (
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400" data-testid="rounds-hash-chain">
                    <span className="flex items-center gap-1.5"><Link2 className="h-3 w-3" /> Hash-chained · SHA-256 {report.contentHash.slice(0, 16)}{'\u2026'}</span>
                    <a href={`${API}/clinician/rounds/verify?hash=${report.contentHash}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 font-semibold text-emerald-600 hover:text-emerald-800" data-testid="rounds-verify-link">
                      <BadgeCheck className="h-3.5 w-3.5" /> Verify independently
                    </a>
                  </div>
                )}
              </div>

              {/* SBAR */}
              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="rounds-sbar">
                <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-slate-800">
                  <ClipboardList className="h-5 w-5 text-teal-600" /> SBAR Hand-off
                </h3>
                {[
                  ['Situation', report.sbar?.situation, 'rounds-sbar-situation'],
                  ['Background', report.sbar?.background, 'rounds-sbar-background'],
                  ['Assessment', report.sbar?.assessment, 'rounds-sbar-assessment'],
                  ['Recommendation', report.sbar?.recommendation, 'rounds-sbar-recommendation'],
                ].map(([label, val, tid]) => (
                  <div key={label} className="mb-3 last:mb-0">
                    <p className="text-xs font-bold uppercase tracking-wide text-teal-700">{label}</p>
                    <p className="mt-0.5 text-sm leading-relaxed text-slate-700" data-testid={tid}>{val || '\u2014'}</p>
                  </div>
                ))}
              </div>

              {/* Ranked differentials */}
              {report.ranked_differentials?.length > 0 && (
                <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="rounds-differentials">
                  <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-slate-800">
                    <Scale className="h-5 w-5 text-indigo-600" /> Bayesian-ranked differentials
                  </h3>
                  <div className="space-y-2">
                    {report.ranked_differentials.slice(0, 8).map((d, i) => (
                      <div key={i} className="flex items-center gap-3" data-testid="rounds-differential-row">
                        <span className="w-12 shrink-0 text-right text-sm font-bold text-indigo-700">
                          {d.post_test != null ? `${Math.round(d.post_test * 100)}%` : '\u2014'}
                        </span>
                        <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                          <div className="h-full rounded-full bg-indigo-500" style={{ width: `${Math.round((d.post_test || 0) * 100)}%` }} />
                        </div>
                        <span className="w-1/2 shrink-0 truncate text-sm text-slate-700" title={d.dx}>{d.dx}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Conflicts */}
              {report.conflicts?.length > 0 && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-5" data-testid="rounds-conflicts">
                  <h3 className="mb-2 flex items-center gap-2 text-base font-semibold text-amber-800">
                    <ShieldAlert className="h-5 w-5" /> Conflicts to reconcile
                  </h3>
                  <ul className="list-disc space-y-1 pl-5 text-sm text-amber-800">
                    {report.conflicts.map((c, i) => <li key={i}>{c.note}</li>)}
                  </ul>
                </div>
              )}

              {/* Critical actions */}
              {report.critical_actions?.length > 0 && (
                <div className="rounded-xl border border-red-200 bg-white p-5 shadow-sm" data-testid="rounds-critical-actions">
                  <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-red-700">
                    <AlertTriangle className="h-5 w-5" /> High-priority actions
                  </h3>
                  <div className="space-y-2">
                    {report.critical_actions.map((a, i) => <FlagRow key={i} f={a} />)}
                  </div>
                </div>
              )}

              {/* Findings by channel */}
              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="rounds-by-channel">
                <h3 className="mb-3 text-base font-semibold text-slate-800">All findings by channel</h3>
                {['symptom', 'exam', 'ecg', 'labs'].map((ch) => {
                  const items = report.by_channel?.[ch] || [];
                  const cm = CHANNEL_META[ch];
                  const Icon = cm.icon;
                  return (
                    <details key={ch} className="group mb-2 rounded-lg border border-slate-200 last:mb-0" data-testid={`rounds-channel-${ch}`}>
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 select-none">
                        <span className="flex items-center gap-2">
                          <Icon className={`h-4 w-4 ${cm.accent}`} />
                          <span className="text-sm font-medium text-slate-700">{cm.label}</span>
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{items.length}</span>
                        </span>
                        <ChevronDown className="h-4 w-4 text-slate-400 transition-transform group-open:rotate-180" />
                      </summary>
                      <div className="space-y-2 px-4 pb-4">
                        {items.length === 0
                          ? <p className="text-xs text-slate-400">No findings on this channel.</p>
                          : items.map((f, i) => <FlagRow key={i} f={f} />)}
                      </div>
                    </details>
                  );
                })}
              </div>
            </div>
          )}
          <div className="mt-5">
            <LedgerTimeline ledger={ledger} currentHash={report?.contentHash} />
          </div>
        </div>
      </div>
    </div>
  );
}
